import { useState, useEffect } from "react";
import { useNavigate, Navigate, Link, useSearchParams } from "react-router-dom";
import { Loader2, UserCircle2, MailCheck, AlertTriangle } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../admin/AuthContext";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function PortalAuth() {
  const { user, login, register } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", company: "", email: "", phone: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [verifyNotice, setVerifyNotice] = useState(false);

  if (user && !verifyNotice) return <Navigate to="/portal" replace />;

  const h = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "login") {
        await login(form.email, form.password);
        navigate("/portal");
      } else {
        const data = await register(form);
        if (data.verification_required) setVerifyNotice(true);
        else navigate("/portal");
      }
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  if (verifyNotice) {
    return (
      <div className="min-h-screen hero-bg flex items-center justify-center px-4">
        <div data-testid="portal-verify-notice" className="bg-white rounded-2xl p-10 shadow-2xl max-w-md text-center">
          <div className="w-14 h-14 mx-auto rounded-full bg-[var(--pg-accent)]/10 text-[var(--pg-accent)] flex items-center justify-center"><MailCheck size={26} /></div>
          <h1 className="mt-5 font-[Poppins] font-bold text-[22px] text-[var(--pg-navy)]">Check your email</h1>
          <p className="mt-3 text-[14.5px] text-slate-500">We sent a verification link to <strong className="text-[var(--pg-navy)]">{form.email}</strong>. Click it to activate your Client Portal access.</p>
          <Link to="/" className="btn-primary mt-6 inline-flex">Back to Website</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen hero-bg flex items-center justify-center px-4 py-16">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="text-white font-[Poppins] font-bold text-[22px] tracking-tight">PATRICK GLOBAL</Link>
          <div className="text-[11px] tracking-[0.2em] text-[var(--pg-accent)] uppercase font-semibold mt-1">Client Portal</div>
        </div>
        <form data-testid="portal-auth-form" onSubmit={submit} className="bg-white rounded-2xl p-8 shadow-2xl">
          <div className="w-12 h-12 rounded-xl bg-[var(--pg-navy)] text-white flex items-center justify-center mb-5"><UserCircle2 size={22} /></div>
          <div className="flex gap-2 mb-5">
            <button type="button" data-testid="portal-mode-login" onClick={() => { setMode("login"); setError(""); }}
              className={`flex-1 py-2.5 rounded-lg font-[Poppins] font-semibold text-[13.5px] transition-colors ${mode === "login" ? "bg-[var(--pg-navy)] text-white" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}>Sign In</button>
            <button type="button" data-testid="portal-mode-register" onClick={() => { setMode("register"); setError(""); }}
              className={`flex-1 py-2.5 rounded-lg font-[Poppins] font-semibold text-[13.5px] transition-colors ${mode === "register" ? "bg-[var(--pg-navy)] text-white" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}>Create Account</button>
          </div>
          {mode === "register" && (
            <>
              <div className="mb-4">
                <label className="pg-label" htmlFor="portal-name">Full Name *</label>
                <input id="portal-name" data-testid="portal-name-input" className="pg-input" value={form.name} onChange={h("name")} required />
              </div>
              <div className="mb-4 grid grid-cols-2 gap-3">
                <div>
                  <label className="pg-label" htmlFor="portal-company">Company</label>
                  <input id="portal-company" data-testid="portal-company-input" className="pg-input" value={form.company} onChange={h("company")} />
                </div>
                <div>
                  <label className="pg-label" htmlFor="portal-phone">Phone</label>
                  <input id="portal-phone" data-testid="portal-phone-input" className="pg-input" value={form.phone} onChange={h("phone")} />
                </div>
              </div>
            </>
          )}
          <div className="mb-4">
            <label className="pg-label" htmlFor="portal-email">Email *</label>
            <input id="portal-email" data-testid="portal-email-input" type="email" className="pg-input" value={form.email} onChange={h("email")} required autoComplete="username" />
          </div>
          <div>
            <label className="pg-label" htmlFor="portal-password">Password * {mode === "register" && <span className="text-slate-400 font-normal">(min 8 characters)</span>}</label>
            <input id="portal-password" data-testid="portal-password-input" type="password" className="pg-input" value={form.password} onChange={h("password")} required minLength={mode === "register" ? 8 : undefined} autoComplete={mode === "login" ? "current-password" : "new-password"} />
          </div>
          {error && <p data-testid="portal-auth-error" className="mt-4 text-[13.5px] text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{error}</p>}
          <button data-testid="portal-auth-submit" type="submit" disabled={loading} className="btn-primary w-full justify-center mt-6 disabled:opacity-70">
            {loading ? <Loader2 className="animate-spin" size={18} /> : mode === "login" ? "Sign In" : "Create Account"}
          </button>
          <p className="mt-5 text-[12.5px] text-slate-500 text-center">
            Use the email address from your RFQ submissions to see your quotes and orders.
          </p>
          <p className="mt-2 text-[12.5px] text-center">
            <Link to="/supplier/login" data-testid="portal-supplier-link" className="text-[var(--pg-accent)] font-[Poppins] font-semibold hover:underline">Are you a supplier? Go to the Supplier Portal →</Link>
          </p>
        </form>
      </div>
    </div>
  );
}

export function VerifyEmail() {
  const [params] = useSearchParams();
  const [state, setState] = useState("verifying");
  useEffect(() => {
    const token = params.get("token");
    if (!token) { setState("error"); return; }
    api.post("/auth/verify-email", { token }).then(() => setState("done")).catch(() => setState("error"));
  }, [params]);
  return (
    <div className="min-h-screen hero-bg flex items-center justify-center px-4">
      <div data-testid="verify-email-panel" className="bg-white rounded-2xl p-10 shadow-2xl max-w-md text-center">
        {state === "verifying" && <><Loader2 className="animate-spin mx-auto text-[var(--pg-accent)]" size={30} /><p className="mt-4 text-[15px] text-slate-500">Verifying your email...</p></>}
        {state === "done" && (
          <>
            <div className="w-14 h-14 mx-auto rounded-full bg-green-100 text-green-600 flex items-center justify-center"><MailCheck size={26} /></div>
            <h1 className="mt-5 font-[Poppins] font-bold text-[22px] text-[var(--pg-navy)]">Email verified</h1>
            <p className="mt-3 text-[14.5px] text-slate-500">Your account is active. You can now access your Client Portal.</p>
            <Link to="/portal" data-testid="verify-goto-portal" className="btn-primary mt-6 inline-flex">Go to My Portal</Link>
          </>
        )}
        {state === "error" && (
          <>
            <div className="w-14 h-14 mx-auto rounded-full bg-red-100 text-red-600 flex items-center justify-center"><AlertTriangle size={26} /></div>
            <h1 className="mt-5 font-[Poppins] font-bold text-[22px] text-[var(--pg-navy)]">Verification failed</h1>
            <p className="mt-3 text-[14.5px] text-slate-500">This link is invalid or has expired. Please register again or contact us at 281-901-9914.</p>
            <Link to="/portal/login" className="btn-primary mt-6 inline-flex">Back to Sign In</Link>
          </>
        )}
      </div>
    </div>
  );
}
